import { z } from "zod";
import { tooBigMessage } from "@/lib/zod-number-messages";

// Máy chia đơn Profile — khớp components["schemas"]["Dispatch*"] trong
// src/lib/api/generated/schema.ts (backend/internal/core/orderdispatch).
//
// LUẬT: field nào BE gắn `omitempty` thì Ở ĐÂY PHẢI `.optional()`; thiếu một
// cái là zod parse fail và cả màn trắng (bài học trang /promos). Đã tra tận
// nơi 27/08: trong `SlotRow` (repo.go) KHÔNG field nào có `omitempty` — ba cột
// NULL-được (staff_id/staff_name/assigned_via) được COALESCE thành chuỗi RỖNG,
// nên "chưa có người" là rỗng chứ KHÔNG phải vắng khoá. Cả struct chỉ có
// `HistoryEvent.Note` mang `omitempty` (model.go).

export const DispatchSlotStateSchema = z.enum([
  "pending_shift", // tuần chưa có ca duyệt, suất nằm chờ
  // Đã MỜI cộng tác viên, chờ họ bấm nhận trước hạn. Cột lương trên đơn CHƯA
  // ghi tên ai — mời khác gán.
  "awaiting_ack",
  "assigned",
  "needs_staff", // suất ĐỎ cần người
  "done",
  "cancelled",
]);
export type DispatchSlotState = z.infer<typeof DispatchSlotStateSchema>;

export const DispatchHistoryTypeSchema = z.enum([
  "assigned",
  "returned",
  "reassigned",
  "needs_staff",
  "pending_shift",
  "pickup",
  "override",
  "closed",
  // Năm loại của vòng chờ-nhận và bảng ảnh thẻ (03/09/2026).
  "offered",
  "acked",
  "declined",
  "ack_expired",
  "board_swap",
  // Máy đã báo quản lý người giữ suất chưa chấm công (14/09/2026) — ê-kíp giữ nguyên.
  "no_checkin_warned",
]);
export type DispatchHistoryType = z.infer<typeof DispatchHistoryTypeSchema>;

/** Một dòng lịch sử suất — ai đổi, lúc nào, vì sao. */
export const DispatchHistoryEventSchema = z.object({
  type: DispatchHistoryTypeSchema,
  at: z.string(),
  /** Mã nhân sự, hoặc "machine" khi máy tự làm. */
  by: z.string(),
  /** Máy ghi vì sao chọn, người ghi lý do gán/đổi. Field omitempty DUY NHẤT. */
  note: z.string().optional(),
});
export type DispatchHistoryEvent = z.infer<typeof DispatchHistoryEventSchema>;

export const DispatchSlotKindSchema = z.enum(["photo", "makeup"]);
export type DispatchSlotKind = z.infer<typeof DispatchSlotKindSchema>;

export const DispatchSlotSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  slot: DispatchSlotKindSchema,
  state: DispatchSlotStateSchema,
  // Rỗng khi suất chưa có người — RỖNG, không phải vắng khoá.
  staffId: z.string(),
  staffName: z.string(),
  // machine | self_pickup | manual | board; rỗng khi chưa gán.
  assignedVia: z.string(),
  returnCount: z.number().int(),
  history: z.array(DispatchHistoryEventSchema),
  customer: z.string(),
  branch: z.string(),
  // Rỗng khi đơn chưa có ngày/giờ (đơn giữ chỗ chờ khách chốt).
  shootDate: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  productLabel: z.string(),
  // Hai mốc chờ-nhận là hai khoá `omitempty` DUY NHẤT của SlotRow: suất chưa
  // mời thì máy chủ BỎ HẲN khoá, không gửi null.
  ackDeadlineAt: z.string().optional(),
  ackedAt: z.string().optional(),
  // Ba khoá dưới máy chủ LUÔN gửi ⇒ bắt buộc, KHÔNG .default().
  // `.default([])` ở đây là nới hợp đồng: mảng vắng sẽ lặng lẽ thành rỗng và
  // màn hình vẽ mọi đơn thành "không nhãn" mà không ai biết hợp đồng đã lệch.
  skippedStaff: z.array(z.string()),
  /** Nhãn của ĐƠN (không phải của suất) — bảng dùng để tô màu. */
  tags: z.array(z.string()),
  /** Đơn KOL. Nằm ở cột riêng, KHÔNG phải chuỗi "kol" trong `tags`. */
  isKol: z.boolean(),
  /** Đơn mang nhãn cần người được chỉ định (gồm cả KOL). */
  needsDesignee: z.boolean(),
  // The order's product line (e.g. "profile") and the concept the customer
  // picked — EMPTY, not absent, when none was picked. "My slots" uses the pair
  // to attach the right guide to the shift (decision 0128). The server always
  // sends both ⇒ required, no .default().
  category: z.string(),
  selectedConcept: z.string(),
});
export type DispatchSlot = z.infer<typeof DispatchSlotSchema>;

/**
 * Màn "Suất của tôi" trên điện thoại.
 *
 * ⚠️ `needsStaff` ở ĐÂY là MẢNG suất, còn `needsStaff` của bảng cơ sở bên dưới
 * là SỐ đếm — cùng tên, khác kiểu, đúng như hợp đồng máy chủ. Đừng "thống
 * nhất" hai cái làm một.
 */
export const DispatchMyViewSchema = z.object({
  mine: z.array(DispatchSlotSchema),
  // Suất ĐỎ đã lọc THÔ theo khối và tầm với cơ sở; điều kiện tinh (ca phủ,
  // trùng lịch, trần) chỉ kiểm lúc bấm nhặt.
  needsStaff: z.array(DispatchSlotSchema),
  // autoAccept — người xem đang bật "tự động nhận suất máy giao".
  // canAutoAccept — người xem là cộng tác viên (chỉ họ có công tắc, QĐ 0142 §6.4).
  autoAccept: z.boolean(),
  canAutoAccept: z.boolean(),
});
export type DispatchMyView = z.infer<typeof DispatchMyViewSchema>;

/**
 * Bảng suất một cơ sở trong một ngày. Cố ý KHÔNG mang `branch`/`date` (khác
 * StoreBoardView): màn hình đã tự giữ hai giá trị đó vì chính nó gửi lên.
 */
export const DispatchBoardViewSchema = z.object({
  slots: z.array(DispatchSlotSchema),
  needsStaff: z.number().int(),
});
export type DispatchBoardView = z.infer<typeof DispatchBoardViewSchema>;

/**
 * Ba cảnh báo MỀM cho hộp thoại gán tay. KHÔNG phải kết quả kiểm hợp lệ:
 * người ngoài nhóm ứng viên (khác khối, khác cơ sở) trả về "sạch" vì phần
 * chặn cứng thuộc về Override. Đừng hiển thị thành "đã kiểm hợp lệ ✓".
 *
 * Cả ba đều BẮT BUỘC: `shiftCovers=false` là cảnh báo THẬT ("ngoài ca"), để
 * `.optional()` thì máy chủ quên gửi sẽ thành undefined và màn hình đọc ra
 * "không có cảnh báo" — im lặng đúng vào chiều nguy hiểm.
 */
export const DispatchPrecheckSchema = z.object({
  busy: z.boolean(),
  shiftCovers: z.boolean(),
  overCap: z.boolean(),
  // Chứng chỉ chuyên ngành (QĐ 0101): hai trường này mới thêm nên tuỳ chọn để
  // máy chủ cũ không làm đỏ cả precheck; cửa CHẶN thật nằm ở PUT /orders.
  certMissing: z.boolean().optional().default(false),
  certSpecialty: z.string().optional().default(""),
});
export type DispatchPrecheck = z.infer<typeof DispatchPrecheckSchema>;

// ── Sổ điểm · cấu hình · chỉ định · nhãn (03/09/2026) ────────────────

export const DispatchPointKindSchema = z.enum([
  "decline",
  "no_response",
  "release",
  "release_late",
  "pickup",
  // Đợt 2 (QĐ 0142): máy tự trừ khi đến muộn; adjust là dòng BÙ khi quản lý
  // gỡ một dòng phạt — đọc được trong sổ, không phải hành vi của người.
  "late_arrival",
  "adjust",
  // QĐ 0209: quản lý ĐỔI GÁN một suất, chọn lý do thuộc nhóm "lỗi của thợ".
  //
  // 🔴 Thiếu hai khoá này thì đây là `z.enum` nên CẢ SỔ ĐIỂM của người có một
  // dòng như vậy không đọc được — không phải hiện xấu một dòng, mà vỡ cả
  // trang. Cùng bài học với bảng nhãn vai ở thanh bên (25/09/2026).
  "override_no_show",
  "override_personal",
]);
export type DispatchPointKind = z.infer<typeof DispatchPointKindSchema>;

/** Một dòng sổ điểm hành vi. `slotId`/`orderId` là omitempty ở máy chủ. */
export const DispatchPointRowSchema = z.object({
  id: z.string(),
  staffId: z.string(),
  kind: DispatchPointKindSchema,
  delta: z.number().int(),
  slotId: z.string().optional(),
  orderId: z.string().optional(),
  note: z.string(),
  // voidOf — dòng adjust này gỡ dòng nào; máy ghi thì không có.
  voidOf: z.string().optional(),
  createdAt: z.string(),
});
export type DispatchPointRow = z.infer<typeof DispatchPointRowSchema>;

export const DispatchPointsViewSchema = z.object({
  total: z.number().int(),
  windowDays: z.number().int(),
  rows: z.array(DispatchPointRowSchema),
});
export type DispatchPointsView = z.infer<typeof DispatchPointsViewSchema>;

/**
 * Sổ điểm của MỘT nhân sự — nền của màn Điểm hành vi. Khác
 * `DispatchPointsViewSchema` (sổ của chính mình) ở ba trường nhận dạng người
 * bị trừ: màn của quản lý xem nhiều người nên phải biết đang xem ai.
 */
export const DispatchPointsStaffViewSchema = z.object({
  staffId: z.string(),
  staffName: z.string(),
  branch: z.string(),
  total: z.number().int(),
  windowDays: z.number().int(),
  rows: z.array(DispatchPointRowSchema),
});
export type DispatchPointsStaffView = z.infer<typeof DispatchPointsStaffViewSchema>;

export const DispatchPointSummarySchema = z.object({
  staffId: z.string(),
  staffName: z.string(),
  branch: z.string(),
  department: z.string(),
  total: z.number().int(),
  declines: z.number().int(),
  misses: z.number().int(),
  releases: z.number().int(),
  pickups: z.number().int(),
});
export type DispatchPointSummary = z.infer<typeof DispatchPointSummarySchema>;

export const DispatchWindowSchema = z.object({
  start: z.string().regex(/^\d{2}:\d{2}$/),
  end: z.string().regex(/^\d{2}:\d{2}$/),
});

/**
 * Cấu hình máy chia. Ngưỡng ở đây phải TRÙNG ngưỡng máy chủ: máy chủ TỪ CHỐI
 * 400 chứ không lặng lẽ sửa, nên màn hình nới rộng hơn là để người dùng gõ
 * một số rồi lãnh lỗi mà không hiểu vì sao.
 *
 * ⚠️ MỌI field đều BẮT BUỘC, và đó là chuyện sống còn chứ không phải khó tính:
 * PUT `/dispatch/config` thay TRỌN GÓI (`MutateSetting` ghi đè cả khoá), nên
 * một `.optional()` ở đây, hay một màn hình chỉ gửi lên vài field mình biết,
 * là lặng lẽ reset những tham số người dùng chưa hề đụng tới. Màn nào sửa cấu
 * hình cũng phải NẠP TRỌN bản hiện có rồi gửi TRỌN bản đó về.
 *
 * `disabledBranches` — cơ sở đang TẮT máy, KHÔNG phải cơ sở đang bật. Đặt theo
 * phía "tắt" để danh sách rỗng = máy chạy khắp nơi; ai đọc nhầm polarity là
 * tắt máy toàn hệ mà giao diện vẫn trông bình thường.
 */
export const DispatchConfigSchema = z.object({
  paused: z.boolean(),
  disabledBranches: z.array(z.string()),
  defaultWindow: DispatchWindowSchema,
  ackHours: z.number().int().min(1),
  ackMinLeadHours: z.number().int().min(0),
  boardSwapWaitMinutes: z.number().int().min(0),
  noCheckinWarnMinutes: z.number().int().min(0),
  sweepDays: z.number().int().min(1).max(30),
  points: z.record(z.string(), z.number().int()),
  floorPerWeek: z.number().int(),
  floorMinShifts: z.number().int(),
  capPerDay: z.number().int().min(1),
  lateReturnHours: z.number().int(),
  scoreWindowDays: z.number().int().min(1),
  awardWindowDays: z.number().int().min(1),
  bayesK: z.number().int().min(0),
  awardUnit: z.number(),
  awardCap: z.number(),
  lateReturnUnit: z.number(),
  lateReturnCap: z.number(),
  // Bảy núm cân bằng (đợt 2, QĐ 0142). Máy chủ từ chối số âm (400) — kẹp
  // cùng ngưỡng để người dùng thấy lỗi ngay ở ô, không phải sau khi bấm lưu.
  loadPenaltyPerSlot: z.number().int().min(0),
  tieMargin: z.number().int().min(0),
  pointWeight: z.number().int().min(0),
  readyBonus: z.number().int().min(0),
  repeatCustomerBonus: z.number().int().min(0),
  sameDayBonus: z.number().int().min(0),
  lateArrivalMinutes: z.number().int().min(0),
  // Tinh Thạch & tầng bậc (spec 2026-09-20). Chưa có ô nào trên màn nhưng vẫn
  // phải có mặt ở đây: thiếu là zod lặng lẽ bỏ lúc đọc, và mỗi lần ai bấm Lưu
  // (hay Chạy thử) là bảy cài đặt này về 0 / tắt (lỗi 24/09/2026). Máy chủ
  // KHÔNG kẹp các số này — đừng thêm .min(): chặn hẹp hơn máy chủ là một bản
  // cấu hình hợp lệ đọc về cũng hỏng, cả màn trắng.
  crystalEnabled: z.boolean(),
  tierTieringEnabled: z.boolean(),
  crossBranchEnabled: z.boolean(),
  specialShiftBonus: z.number().int(),
  crossBranchBonus: z.number().int(),
  lineNumbersEnabled: z.boolean(),
  travelBufferMinutes: z.number().int(),
  // Năm núm của QĐ 0209 — chia ảnh thẻ theo "ai SẮP rảnh lúc khách tới".
  grabEnabled: z.boolean(),
  openLeadMinutes: z.number().int(),
  handoverBufferMinutes: z.number().int(),
  queueAheadCap: z.number().int(),
  // QĐ 0232: hạn bấm nhận của cộng tác viên ở dòng "sắp rảnh" (phút).
  grabAckMinutes: z.number().int(),
  overrunFlagRatio: z.number(),
  // QĐ 0257: công tắc "chia hai máy" — chỉ đọc ở cấu hình máy 1, máy 2 trả số
  // của máy 1. Tắt = một máy chia như trước.
  twoMachinesEnabled: z.boolean(),
  // QĐ 0258: khách ảnh thẻ có hẹn tính như tới sớm N phút (máy 2). CEO chốt 30.
  bookedAheadMinutes: z.number().int().min(0),
});
export type DispatchConfig = z.infer<typeof DispatchConfigSchema>;

/**
 * Câu báo tiếng Việt cho các ô số của form cài đặt (lỗi 23/09/2026, anh chốt
 * câu chữ cùng ngày). Form truyền hàm này vào `zodResolver`; lược đồ ở trên cố
 * ý KHÔNG mang câu riêng từng ô vì nó còn dùng để đọc bản máy chủ trả về.
 *
 * Thiếu hàm này thì dưới ô hiện nguyên văn câu mặc định TIẾNG ANH của zod:
 * xoá trắng ô → "Invalid input: expected number, received NaN", gõ 0 vào ô
 * phải từ 1 → "Too small: expected number to be >=1". Ô số thêm vào lược đồ
 * về sau tự có câu tiếng Việt, không phải nhớ viết thêm. Lỗi không phải của
 * ô số thì trả undefined — zod dùng câu của nó như cũ.
 */
export const dispatchConfigErrorMap: z.core.$ZodErrorMap = (issue) => {
  if (issue.code === "invalid_type" && (issue.expected === "number" || issue.expected === "int")) {
    return issue.expected === "int" ? "Nhập số nguyên" : "Nhập một số";
  }
  if (issue.code === "too_small" && issue.origin === "number") {
    return Number(issue.minimum) === 0 ? "Không được là số âm" : `Phải từ ${issue.minimum} trở lên`;
  }
  if (issue.code === "too_big" && issue.origin === "number") {
    return `Tối đa ${issue.maximum}`;
  }
  // `.int()` còn chặn số vượt khoảng an toàn (±9.007.199.254.740.991) và báo
  // với origin "int", không phải "number" — gõ thừa vài số 0, hay số âm dài,
  // là tới (lỗi 24/09/2026). Câu nằm ngay dưới ô nên không lặp tên ô.
  if ((issue.code === "too_big" || issue.code === "too_small") && issue.origin === "int") {
    return tooBigMessage("Số");
  }
  return undefined;
};

export const DispatchDeptSchema = z.enum(["photo", "makeup", "retouch"]);
export type DispatchDept = z.infer<typeof DispatchDeptSchema>;

export const DispatchDesigneeSchema = z.object({
  tagCode: z.string(),
  dept: DispatchDeptSchema,
  staffId: z.string(),
  staffName: z.string(),
});
export type DispatchDesignee = z.infer<typeof DispatchDesigneeSchema>;

export const DispatchSweepSummarySchema = z.object({
  date: z.string(),
  orders: z.number().int(),
  assigned: z.number().int(),
  awaitingAck: z.number().int(),
  needsStaff: z.number().int(),
  pendingShift: z.number().int(),
});
export type DispatchSweepSummary = z.infer<typeof DispatchSweepSummarySchema>;

/** Một nhãn trong danh mục. `virtual` là cờ của hệ, màn cài đặt không đổi. */
export const OrderTagRuleSchema = z.object({
  code: z.string(),
  label: z.string(),
  needsDesignee: z.boolean(),
  virtual: z.boolean(),
  active: z.boolean(),
  sort: z.number().int(),
  /**
   * Đơn mang nhãn này là CA KHÓ của hậu kỳ (QĐ 0227). Mặc định `false` khi máy
   * chủ chưa gửi ô này, để màn không vỡ lúc hai bản lên lệch nhau vài phút.
   */
  retouchHardJob: z.boolean().default(false),
});
export type OrderTagRule = z.infer<typeof OrderTagRuleSchema>;

// ── Vì sao tôi chưa được suất (QĐ 0142 §7.2) ────────────────────────────────
//
// ⚠️ Không khoá nào ở đây mang số của NGƯỜI KHÁC, và đừng thêm: máy chủ cố ý
// chỉ gửi tên người nhận + tên phần điểm họ nhỉnh hơn. "Kém bao nhiêu điểm"
// cộng với số của chính mình là ra số của họ (plan đợt 3, lệch 16).

/** won = máy chọn bạn · ranked = đủ điều kiện, chưa tới lượt · excluded = bị loại · absent = không có trong lần xếp · manual = người xếp. */
export const DispatchWhyOutcomeSchema = z.enum(["won", "ranked", "excluded", "absent", "manual"]);
export type DispatchWhyOutcome = z.infer<typeof DispatchWhyOutcomeSchema>;

/** Sáu khoản cộng thành điểm hiệu quả của CHÍNH người xem. Máy chủ không làm tròn. */
export const DispatchEffectivePartsSchema = z.object({
  stars: z.number(),
  load: z.number(),
  points: z.number(),
  ready: z.number(),
  repeat: z.number(),
  sameDay: z.number(),
});
export type DispatchEffectiveParts = z.infer<typeof DispatchEffectivePartsSchema>;

// Sáu khoá cuối là `omitempty` ở mywhy.go ⇒ .optional(); `reason` luôn có.
export const DispatchMyWhyRowSchema = z.object({
  slotId: z.string(),
  slot: DispatchSlotKindSchema,
  branch: z.string(),
  branchName: z.string(),
  // "HH:MM"; rỗng khi đơn chưa có giờ.
  start: z.string(),
  end: z.string(),
  outcome: DispatchWhyOutcomeSchema,
  reason: z.string(),
  rank: z.number().int().optional(),
  of: z.number().int().optional(),
  effective: z.number().optional(),
  parts: DispatchEffectivePartsSchema.optional(),
  winnerName: z.string().optional(),
  betterAt: z.string().optional(),
});
export type DispatchMyWhyRow = z.infer<typeof DispatchMyWhyRowSchema>;

export const DispatchMyWhySchema = z.object({
  date: z.string(),
  rows: z.array(DispatchMyWhyRowSchema),
});
export type DispatchMyWhy = z.infer<typeof DispatchMyWhySchema>;

// ── Chạy thử (đợt 3, spec §7.3) — khớp DispatchSimSide/SimResult/Simulation ──

export const DispatchSimSideSchema = z.object({
  label: z.string(),
  perStaff: z.record(z.string(), z.number().int()),
  maxPerStaff: z.number().int(),
  minPerStaff: z.number().int(),
  spread: z.number().int(),
  unassignable: z.number().int(),
  staffed: z.number().int(),
  top3Share: z.number().min(0).max(1),
  floorSlots: z.number().int(),
  ties: z.number().int(),
});
export type DispatchSimSide = z.infer<typeof DispatchSimSideSchema>;

export const DispatchSimResultSchema = z.object({
  windowDays: z.number().int(),
  slots: z.number().int(),
  changed: z.number().int(),
  matched: z.number().int(),
  current: DispatchSimSideSchema,
  trial: DispatchSimSideSchema,
  same: z.boolean(),
  estimate: z.boolean(),
});
export type DispatchSimResult = z.infer<typeof DispatchSimResultSchema>;

export const DispatchSimulationSchema = z.object({
  id: z.string(),
  windowDays: z.number().int(),
  status: z.enum(["queued", "running", "done", "failed"]),
  result: DispatchSimResultSchema.optional(),
  error: z.string().optional(),
  createdAt: z.string(),
  startedAt: z.string().optional(),
  finishedAt: z.string().optional(),
});
export type DispatchSimulation = z.infer<typeof DispatchSimulationSchema>;

export const DispatchLineRuleSchema = z.object({
  code: z.string(),
  name: z.string(),
  needsDispatch: z.boolean(),
  requiresCheckin: z.boolean(),
  // QĐ 0257: loại việc của dòng cho hai máy chia; rỗng = chưa khai.
  jobKind: z.string(),
});
export type DispatchLineRule = z.infer<typeof DispatchLineRuleSchema>;
